import { useEffect, useRef, useState } from 'react'

import {
  useGetQuotingSmartCensusParseJobsIdInputFileContents,
  useGetQuotingSmartCensusParseJobsIdProspectEmployees,
} from 'utils/web_api'
import type { QuotingSmartCensusParseJobStatus } from 'utils/web_api.schemas'

import InputCensusCsvSheet from './InputCensusCsvSheet'
import OutputProspectEmployeeSheet from './OutputProspectEmployeeSheet'
import SheetSkeleton from './SheetSkeleton'

interface InputOutputComparisonTableProps {
  jobId: string
  status: QuotingSmartCensusParseJobStatus
}

export default function InputOutputComparisonTable ({ jobId, status }: InputOutputComparisonTableProps) {
  const { data: inputFileContents, isLoading: isLoadingInputFileContents } = useGetQuotingSmartCensusParseJobsIdInputFileContents(jobId, { raw_input_file: false }, {
    query: {
      enabled: status === 'completed' || status === 'in_progress',
      retry: (_failureCount, error) => {
        // 404 means the file has not yet been created, but should become attached at some point during the background job
        return ((error as any)?.status === 404) && status === 'in_progress'
      },
    },
  })

  const { data: prospectEmployees, isLoading: isLoadingProspectEmployees, refetch: refetchProspectEmployees } = useGetQuotingSmartCensusParseJobsIdProspectEmployees(jobId, {
    query: {
      enabled: status === 'completed' || status === 'in_progress',
      refetchInterval: () => {
        return status === 'in_progress' ? 2000 : false
      },
    },
  })

  const prevStatusRef = useRef<QuotingSmartCensusParseJobStatus | null>(null)

  // Refetch once more when status transitions to 'completed' to ensure we have final data
  useEffect(() => {
    const prevStatus = prevStatusRef.current
    prevStatusRef.current = status

    if (prevStatus !== null && status === 'completed' && prevStatus !== 'completed') {
      refetchProspectEmployees()
    }
  }, [status, refetchProspectEmployees])

  const [highlightedInputRowIndices, setHighlightedInputRowIndices] = useState<Set<number>>(new Set())
  const [highlightedOutputRowIndices, setHighlightedOutputRowIndices] = useState<Set<number>>(new Set())
  const [inputScrollEnabled, setInputScrollEnabled] = useState(false)
  const [outputScrollEnabled, setOutputScrollEnabled] = useState(false)

  const handleOutputRowHover = (rowIndex: number | null) => {
    if (rowIndex === null || !prospectEmployees) {
      setHighlightedInputRowIndices(new Set())
      setHighlightedOutputRowIndices(new Set())
      setInputScrollEnabled(false)
      return
    }

    // Get the source row indices from the hovered employee's smart_census_parse_info
    const employee = prospectEmployees[rowIndex]
    const sourceRowIndices = employee?.smart_census_parse_info?.input_file_source_row_indices ?? []

    setHighlightedOutputRowIndices(new Set([rowIndex]))
    setHighlightedInputRowIndices(new Set(sourceRowIndices))
    setInputScrollEnabled(true)
    setOutputScrollEnabled(false)
  }

  const handleInputRowHover = (rowIndex: number | null) => {
    if (rowIndex === null || !prospectEmployees) {
      setHighlightedInputRowIndices(new Set())
      setHighlightedOutputRowIndices(new Set())
      setOutputScrollEnabled(false)
      return
    }

    // Find the employee that contains this input row index in its source_row_indices
    for (let i = 0; i < prospectEmployees.length; i++) {
      const employee = prospectEmployees[i]
      const sourceRowIndices = employee?.smart_census_parse_info?.input_file_source_row_indices ?? []

      if (sourceRowIndices.includes(rowIndex)) {
        // Found the employee - highlight it and all its source input rows
        setHighlightedOutputRowIndices(new Set([i]))
        setHighlightedInputRowIndices(new Set(sourceRowIndices))
        setOutputScrollEnabled(true)
        setInputScrollEnabled(false)
        return
      }
    }

    // No matching employee found - just highlight the hovered input row
    setHighlightedInputRowIndices(new Set([rowIndex]))
    setHighlightedOutputRowIndices(new Set())
    setOutputScrollEnabled(false)
  }

  if ((isLoadingInputFileContents || isLoadingProspectEmployees) && status !== 'failed') {
    return (
      <div className='grid grid-cols-2 gap-2'>
        <SheetSkeleton />
        <SheetSkeleton />
      </div>
    )
  } else if (status === 'failed' && !inputFileContents && !prospectEmployees) {
    // only when the job fails early enough that we don't have any data to display back
    return
  }

  return (
    <div className='grid grid-cols-2 gap-2 text-xs'>
      <InputCensusCsvSheet
        data={inputFileContents ?? { headers: [], rows: [] }}
        onRowHover={handleInputRowHover}
        highlightedRowIndices={highlightedInputRowIndices}
        scrollToHighlighted={inputScrollEnabled}
      />
      <OutputProspectEmployeeSheet
        employees={prospectEmployees ?? []}
        inProgress={status === 'in_progress'}
        onRowHover={handleOutputRowHover}
        highlightedRowIndices={highlightedOutputRowIndices}
        scrollToHighlighted={outputScrollEnabled}
      />
    </div>
  )
}
